import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cx } from './primitives.js';
/**
 * A compact indicator that explains itself: the visible short form (`children`) is a button
 * named for screen readers by `label` ("Context 19%"); click/Enter/Space opens an anchored
 * details popover (`title` + `details`), Escape or an outside press closes it and focus returns.
 * Never a modal: it opens beside the chip, not as a bottom sheet.
 */
export function MetricChip({ label, title, details, children, className, testId, }) {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState(null);
    const chip = useRef(null);
    const pop = useRef(null);
    const titleId = useId();
    useLayoutEffect(() => {
        if (!open)
            return;
        const place = () => {
            const r = chip.current?.getBoundingClientRect();
            if (!r)
                return;
            const width = Math.min(360, window.innerWidth - 24);
            const left = Math.max(12, Math.min(r.left, window.innerWidth - width - 12));
            setPos({ left, top: r.bottom + 8, width });
        };
        place();
        window.addEventListener('resize', place);
        return () => window.removeEventListener('resize', place);
    }, [open]);
    useEffect(() => {
        if (!open)
            return;
        const onKey = (e) => {
            if (e.key === 'Escape') {
                setOpen(false);
                chip.current?.focus();
            }
        };
        const onDown = (e) => {
            const t = e.target;
            if (!pop.current?.contains(t) && !chip.current?.contains(t))
                setOpen(false);
        };
        document.addEventListener('keydown', onKey);
        document.addEventListener('pointerdown', onDown);
        return () => {
            document.removeEventListener('keydown', onKey);
            document.removeEventListener('pointerdown', onDown);
        };
    }, [open]);
    useEffect(() => {
        if (open && pos)
            pop.current?.focus();
    }, [open, pos]);
    return (_jsxs(_Fragment, { children: [_jsx("button", { ref: chip, type: "button", className: cx('vk-metric', className), "aria-label": label, "aria-haspopup": "dialog", "aria-expanded": open, title: label, "data-testid": testId, onClick: () => setOpen(!open), children: children }), open && pos
                ? createPortal(_jsxs("div", { ref: pop, className: "vk-metricpop", role: "dialog", "aria-labelledby": titleId, tabIndex: -1, style: { left: pos.left, top: pos.top, width: pos.width }, children: [_jsx("div", { id: titleId, className: "vk-metricpop__title", children: title }), details] }), document.body)
                : null] }));
}
/** Label/value rows for a details popover. `undefined` values are skipped. */
export function MetricRows({ rows }) {
    return (_jsx("dl", { className: "vk-metricpop__rows", children: rows
            .filter(([, v]) => v !== undefined && v !== '')
            .map(([k, v]) => (_jsxs("div", { className: "vk-metricpop__row", children: [_jsx("dt", { children: k }), _jsx("dd", { children: v })] }, k))) }));
}
//# sourceMappingURL=MetricChip.js.map